import React, { useState, useRef } from "react";
import {
  Trash2,
  ChevronUp,
  ChevronDown,
  Plus,
  X,
  GripVertical,
  Loader2,
  Upload,
} from "lucide-react";
import { toast } from "react-toastify";
import { LIMITS, VALID_INPUT_TYPES } from "../../utils/flowConstants";
import { uploadFlowImage } from "../../apis/whatsappFlows";
import { getFooterAutoPayload, getScreenAutoTokens } from "../../utils/flowAutoMapping";

const inputTypeOptions = VALID_INPUT_TYPES.map((t) => ({
  value: t,
  label: t.charAt(0).toUpperCase() + t.slice(1),
}));

const Field = ({ label, children, hint }) => (
  <div className="space-y-1">
    <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">{label}</label>
    {children}
    {hint && <p className="text-[10px] text-gray-400">{hint}</p>}
  </div>
);

const Input = ({ value, onChange, maxLength, placeholder, ...props }) => (
  <div className="relative">
    <input
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full px-2.5 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#048AAD]/40 bg-white"
      {...props}
    />
    {maxLength && (
      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[9px] text-gray-300">
        {(value || "").length}/{maxLength}
      </span>
    )}
  </div>
);

const OptionEditor = ({ options, onChange, maxOptions, label }) => {
  const addOption = () => {
    if (options.length >= maxOptions) return;
    const num = options.length + 1;
    onChange([...options, { id: `option_${num}`, title: `Option ${num}` }]);
  };
  const updateOption = (idx, field, val) => {
    onChange(options.map((o, i) => (i === idx ? { ...o, [field]: val } : o)));
  };
  const removeOption = (idx) => {
    if (options.length <= 1) return;
    onChange(options.filter((_, i) => i !== idx));
  };

  return (
    <Field label={label || "Options"} hint={`Max ${maxOptions}`}>
      <div className="space-y-1.5">
        {options.map((opt, i) => (
          <div key={i} className="flex items-center gap-1.5">
            <input value={opt.id} onChange={(e) => updateOption(i, "id", e.target.value)} placeholder="id"
              className="w-20 px-2 py-1 text-[11px] border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-[#048AAD]/40" />
            <input value={opt.title} onChange={(e) => updateOption(i, "title", e.target.value)} placeholder="Title"
              className="flex-1 px-2 py-1 text-[11px] border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-[#048AAD]/40" />
            <button onClick={() => removeOption(i)} className="p-0.5 text-gray-400 hover:text-red-500" disabled={options.length <= 1}>
              <X size={12} />
            </button>
          </div>
        ))}
        {options.length < maxOptions && (
          <button onClick={addOption} className="flex items-center gap-1 text-[11px] text-[#048AAD] font-medium hover:underline">
            <Plus size={11} /> Add option
          </button>
        )}
      </div>
    </Field>
  );
};

const ComponentEditor = ({ component, index, total, screens, currentScreenId, onUpdate, onRemove, onMove, dragHandleProps }) => {
  const update = (field, value) => onUpdate({ ...component, [field]: value });
  const otherScreens = screens.filter((s) => s.id !== currentScreenId);
  const autoPayload = getFooterAutoPayload({ screens }, currentScreenId);
  const tokenHints = getScreenAutoTokens({ screens }, currentScreenId);
  const [uploading, setUploading] = useState(false);
  const imageInputRef = useRef(null);
  const MAX_IMAGE_SIZE = 5 * 1024 * 1024;

  const insertTokenIntoText = (token) => {
    if (!token) return;
    const nextText = `${component.text || ""}${component.text ? " " : ""}${token}`;
    update("text", nextText);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    if (file.size > MAX_IMAGE_SIZE) {
      toast.error("Image must be 5MB or smaller");
      e.target.value = "";
      return;
    }

    setUploading(true);
    try {
      const url = await uploadFlowImage(file);
      update("src", url);
    } catch (err) {
      toast.error(err?.response?.data?.error || err.message || "Image upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const renderFields = () => {
    switch (component.type) {
      case "TextInput":
        return (
          <div className="grid grid-cols-2 gap-2">
            <Field label="Name" hint={`Max ${LIMITS.TEXT_INPUT_NAME_MAX}`}>
              <Input value={component.name} onChange={(v) => update("name", v)} maxLength={LIMITS.TEXT_INPUT_NAME_MAX} placeholder="field_name" />
            </Field>
            <Field label="Label" hint={`Max ${LIMITS.TEXT_INPUT_LABEL_MAX}`}>
              <Input value={component.label} onChange={(v) => update("label", v)} maxLength={LIMITS.TEXT_INPUT_LABEL_MAX} placeholder="Label" />
            </Field>
            <Field label="Input Type">
              <select value={component["input-type"] || "text"} onChange={(e) => update("input-type", e.target.value)}
                className="w-full px-2.5 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#048AAD]/40 bg-white">
                {inputTypeOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </Field>
            <Field label="Required">
              <label className="flex items-center gap-2 cursor-pointer mt-1">
                <input type="checkbox" checked={component.required || false} onChange={(e) => update("required", e.target.checked)}
                  className="w-3.5 h-3.5 rounded border-gray-300 text-[#048AAD] focus:ring-[#048AAD]/40" />
                <span className="text-xs text-gray-600">Required field</span>
              </label>
            </Field>
          </div>
        );

      case "Dropdown":
        return (
          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <Field label="Name"><Input value={component.name} onChange={(v) => update("name", v)} maxLength={LIMITS.TEXT_INPUT_NAME_MAX} /></Field>
              <Field label="Label"><Input value={component.label} onChange={(v) => update("label", v)} maxLength={LIMITS.TEXT_INPUT_LABEL_MAX} /></Field>
            </div>
            <OptionEditor options={component["data-source"] || []} onChange={(v) => update("data-source", v)} maxOptions={LIMITS.DROPDOWN_OPTIONS_MAX} />
          </div>
        );

      case "RadioButtonsGroup":
        return (
          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <Field label="Name"><Input value={component.name} onChange={(v) => update("name", v)} maxLength={LIMITS.TEXT_INPUT_NAME_MAX} /></Field>
              <Field label="Label"><Input value={component.label} onChange={(v) => update("label", v)} maxLength={LIMITS.TEXT_INPUT_LABEL_MAX} /></Field>
            </div>
            <OptionEditor options={component["data-source"] || []} onChange={(v) => update("data-source", v)} maxOptions={LIMITS.RADIO_OPTIONS_MAX} label="Radio Options" />
          </div>
        );

      case "CheckboxGroup":
        return (
          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <Field label="Name"><Input value={component.name} onChange={(v) => update("name", v)} maxLength={LIMITS.TEXT_INPUT_NAME_MAX} /></Field>
              <Field label="Label"><Input value={component.label} onChange={(v) => update("label", v)} maxLength={LIMITS.TEXT_INPUT_LABEL_MAX} /></Field>
            </div>
            <OptionEditor options={component["data-source"] || []} onChange={(v) => update("data-source", v)} maxOptions={LIMITS.CHECKBOX_OPTIONS_MAX} label="Checkbox Options" />
          </div>
        );

      case "DatePicker":
        return (
          <div className="grid grid-cols-2 gap-2">
            <Field label="Name"><Input value={component.name} onChange={(v) => update("name", v)} maxLength={LIMITS.DATEPICKER_NAME_MAX} /></Field>
            <Field label="Label"><Input value={component.label} onChange={(v) => update("label", v)} maxLength={LIMITS.DATEPICKER_LABEL_MAX} /></Field>
            <Field label="Min Date"><Input value={component["min-date"]} onChange={(v) => update("min-date", v)} placeholder="YYYY-MM-DD" /></Field>
            <Field label="Max Date"><Input value={component["max-date"]} onChange={(v) => update("max-date", v)} placeholder="YYYY-MM-DD" /></Field>
          </div>
        );

      case "TextHeading":
        return (
          <div className="space-y-2">
            <Field label="Heading Text" hint={`Max ${LIMITS.TEXT_HEADING_MAX}`}>
              <Input value={component.text} onChange={(v) => update("text", v)} maxLength={LIMITS.TEXT_HEADING_MAX} placeholder="Heading text" />
            </Field>
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-gray-400 font-semibold uppercase">Insert variable</span>
              <select
                value=""
                onChange={(e) => {
                  insertTokenIntoText(e.target.value);
                  e.target.value = "";
                }}
                className="flex-1 px-2.5 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#048AAD]/40 bg-white font-mono"
              >
                <option value="">Select…</option>
                {tokenHints.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
          </div>
        );

      case "TextSubheading":
        return (
          <Field label="Subheading Text" hint={`Max ${LIMITS.TEXT_SUBHEADING_MAX}`}>
            <Input value={component.text} onChange={(v) => update("text", v)} maxLength={LIMITS.TEXT_SUBHEADING_MAX} placeholder="Subheading text" />
          </Field>
        );

      case "TextBody":
        return (
          <Field label="Body Text" hint={`Max ${LIMITS.TEXT_BODY_MAX}`}>
            <textarea value={component.text || ""} onChange={(e) => update("text", e.target.value)}
              className="w-full px-2.5 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#048AAD]/40 bg-white resize-none"
              rows={3} maxLength={LIMITS.TEXT_BODY_MAX} placeholder="Body text" />
          </Field>
        );

      case "TextCaption":
        return (
          <Field label="Caption Text" hint={`Max ${LIMITS.TEXT_CAPTION_MAX}`}>
            <Input value={component.text} onChange={(v) => update("text", v)} maxLength={LIMITS.TEXT_CAPTION_MAX} placeholder="Small helper text" />
          </Field>
        );

      case "RichText":
        return (
          <Field label="Rich Text" hint={`Max ${LIMITS.RICH_TEXT_MAX}. Use *bold* for formatting.`}>
            <textarea value={component.text || ""} onChange={(e) => update("text", e.target.value)}
              className="w-full px-2.5 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#048AAD]/40 bg-white resize-none font-mono"
              rows={3} maxLength={LIMITS.RICH_TEXT_MAX} placeholder="*Bold* text here" />
          </Field>
        );

      case "Image":
        return (
          <div className="space-y-2">
            <div className="flex items-start gap-3">
              <div className="flex-1 space-y-1.5">
                <Field label="Image URL" hint="Max 5MB &bull; JPG, PNG, WEBP">
                  <Input
                    value={component.src}
                    onChange={(v) => update("src", v)}
                    placeholder="https://example.com/image.png"
                    disabled={uploading}
                  />
                </Field>
                <div className="flex items-center gap-2">
                  <input
                    ref={imageInputRef}
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/webp"
                    className="hidden"
                    onChange={handleImageUpload}
                    disabled={uploading}
                  />
                  <button
                    type="button"
                    onClick={() => imageInputRef.current?.click()}
                    disabled={uploading}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium border border-[#048AAD]/30 text-[#048AAD] bg-[#048AAD]/5 rounded-lg hover:bg-[#048AAD]/10 transition-colors disabled:opacity-50"
                  >
                    {uploading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
                    {uploading ? "Uploading…" : "Upload Image"}
                  </button>
                  <Field label="">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-gray-400">Height (px):</span>
                      <input
                        value={component.height || ""}
                        onChange={(e) => update("height", Number(e.target.value) || undefined)}
                        placeholder="200"
                        type="number"
                        min={0}
                        max={400}
                        className="w-16 px-2 py-1 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#048AAD]/40 bg-white"
                      />
                    </div>
                  </Field>
                </div>
              </div>
              {component.src && (
                <div className="shrink-0 w-16 h-16 rounded-lg overflow-hidden border border-gray-200 bg-gray-50">
                  <img
                    src={component.src}
                    alt="Preview"
                    className="w-full h-full object-cover"
                    onError={(e) => { e.target.style.display = "none"; }}
                  />
                </div>
              )}
            </div>
          </div>
        );

      case "Footer":
        return (
          <div className="grid grid-cols-2 gap-2">
            <Field label="Button Label" hint={`Max ${LIMITS.FOOTER_LABEL_MAX}`}>
              <Input value={component.label} onChange={(v) => update("label", v)} maxLength={LIMITS.FOOTER_LABEL_MAX} placeholder="Next" />
            </Field>
            <Field label="Action">
              <select value={component["on-click-action"]?.name || "navigate"} onChange={(e) => {
                const actionName = e.target.value;
                if (actionName === "complete") update("on-click-action", { name: "complete" });
                else update("on-click-action", { name: "navigate", next: { type: "screen", name: component["on-click-action"]?.next?.name || "" } });
              }} className="w-full px-2.5 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#048AAD]/40 bg-white">
                <option value="navigate">Navigate</option>
                <option value="complete">Complete</option>
              </select>
            </Field>
            {component["on-click-action"]?.name === "navigate" && (
              <div className="col-span-2">
                <Field label="Target Screen">
                  <select value={component["on-click-action"]?.next?.name || ""} onChange={(e) =>
                    update("on-click-action", { name: "navigate", next: { type: "screen", name: e.target.value } })
                  } className="w-full px-2.5 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#048AAD]/40 bg-white">
                    <option value="">Select screen...</option>
                    {otherScreens.map((s) => <option key={s.id} value={s.id}>{s.title || s.id}</option>)}
                  </select>
                </Field>
              </div>
            )}
            <div className="col-span-2 mt-1 border border-gray-200 rounded-lg p-2 bg-gray-50/60">
              <div className="flex items-center justify-between mb-1.5">
                <p className="text-[10px] font-semibold text-gray-500 uppercase">Payload (auto)</p>
                {tokenHints.length > 0 && (
                  <span className="text-[10px] text-gray-400">
                    Use variables like {tokenHints.slice(0, 1)[0]}
                  </span>
                )}
              </div>
              <pre className="text-[10px] text-gray-700 font-mono whitespace-pre-wrap">
                {JSON.stringify(autoPayload, null, 2)}
              </pre>
            </div>
          </div>
        );

      default:
        return <p className="text-xs text-gray-400">Unknown component type</p>;
    }
  };

  const typeLabels = {
    TextInput: "Text Input",
    Dropdown: "Dropdown",
    RadioButtonsGroup: "Radio Buttons",
    CheckboxGroup: "Checkbox Group",
    DatePicker: "Date Picker",
    TextHeading: "Heading",
    TextSubheading: "Subheading",
    TextBody: "Body Text",
    TextCaption: "Caption",
    RichText: "Rich Text",
    Image: "Image",
    Footer: "Footer Button",
  };

  const typeColors = {
    TextInput: "bg-blue-50 text-blue-600 border-blue-200",
    Dropdown: "bg-purple-50 text-purple-600 border-purple-200",
    RadioButtonsGroup: "bg-orange-50 text-orange-600 border-orange-200",
    CheckboxGroup: "bg-green-50 text-green-600 border-green-200",
    DatePicker: "bg-pink-50 text-pink-600 border-pink-200",
    TextHeading: "bg-gray-50 text-gray-600 border-gray-200",
    TextSubheading: "bg-indigo-50 text-indigo-600 border-indigo-200",
    TextBody: "bg-sky-50 text-sky-600 border-sky-200",
    TextCaption: "bg-amber-50 text-amber-600 border-amber-200",
    RichText: "bg-violet-50 text-violet-600 border-violet-200",
    Image: "bg-cyan-50 text-cyan-600 border-cyan-200",
    Footer: "bg-teal-50 text-teal-600 border-teal-200",
  };

  return (
    <div className="waf-comp">
      <div className="waf-comp-head">
        <div className="flex items-center gap-2">
          <button
            {...(dragHandleProps || {})}
            className="p-0.5 text-gray-300 hover:text-gray-500 cursor-grab active:cursor-grabbing rounded"
            tabIndex={-1}
          >
            <GripVertical size={12} />
          </button>
          <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${typeColors[component.type] || "bg-gray-50 text-gray-500"}`}>
            {typeLabels[component.type] || component.type}
          </span>
          {component.name && <span className="text-[10px] text-gray-400 font-mono">{component.name}</span>}
        </div>
        <div className="flex items-center gap-0.5">
          <button onClick={() => onMove(-1)} disabled={index === 0} className="p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-30"><ChevronUp size={13} /></button>
          <button onClick={() => onMove(1)} disabled={index === total - 1} className="p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-30"><ChevronDown size={13} /></button>
          <button onClick={onRemove} className="p-0.5 text-gray-400 hover:text-red-500 ml-1"><Trash2 size={13} /></button>
        </div>
      </div>
      <div className="waf-comp-body">{renderFields()}</div>
    </div>
  );
};

export default ComponentEditor;
